"use client";

import { MaybeBrandPageImageErrorBoundaryWrapper } from "@faire/retailer-visitor-shared/components/UPLT/UPLF/BrandPageImageErrorBoundary/BrandPageImageErrorBoundaryWrapper";
import { Caseline } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/CaseSizeText/Caseline";
import { DebugInfoSection } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/DebugInfoSection/DebugInfoSection";
import { debugInfoSectionPropsILeanProductTileAdapter } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/DebugInfoSection/debugInfoSectionPropsILeanProductTileAdapter";
import { Favorite } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/Favorite/Favorite";
import { productTileFavoritePropsILeanProductTileAdapter } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/Favorite/FavoriteProps";
import { NotifyMe } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/NotifyMe/NotifyMe";
import { notifyMeProductTileAdaptor } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/NotifyMe/NotifyMeUtils";
import { ProductImage } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductImage/ProductTileImage";
import { ExpectedShipDate } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/ExpectedShipDate";
import { ClientFormatMoneyPriceWrapperProps } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/PriceText/ClientFormatMoneyPriceWrapperProps";
import { PriceWithClientFormatMoney } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/PriceText/PriceWithClientFormatMoney";
import { ProductName } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/ProductName";
import { productLinkPropsAdapter } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductLink/Brand/ProductLinkForBrandPageProps";
import { SearchProductLink } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductLink/SearchProductLink";
import { productTileClickProviderPropsILeanProductTileAdapter } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileClickProvider/search/ProductTileClickProviderProps";
import { ProductTileContainer } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileContainer/ProductTileContainer";
import { ProductTileOverlay } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileOverlay/ProductTileOverlay";
import { productTileOverlayProductTileAdaptor } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileOverlay/productTileOverlayUtils";
import { ProductTileTrackingWrapper } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileTrackingWrapper/ProductTileTrackingWrapper";
import { productTileTrackingWrapperPropsILeanProductTileAdapter } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileTrackingWrapper/ProductTileTrackingWrapperProps";
import { ProductTileUpperContainer } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileUpperContainer/ProductTileUpperContainer";
import { QuickAdd } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/QuickAdd/QuickAdd";
import { productTileQuickAddPropsILeanProductTileAdapter } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/QuickAdd/QuickAddProps";
import { TopBadges } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/TopBadges/TopBadges";
import { productTileTopBadgesPropsILeanProductTileAdapter } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/TopBadges/TopBadgesProps";
import { ProductTileContainerRefContextProvider } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/ProductTileContainerRefContextProvider";
import { ProductTileContextProvider } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/ProductTileContextProvider";
import { ProductTileSwatches } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/ProductTileSwatches";
import { ProductTileSurface } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/ProductTileSurface";
import { ProductTileSurfaceContext } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/ProductTileSurfaceContext";
import { useIsLoggedInRetailerOrBrandPreview } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { showProductPromotions } from "@faire/retailer-visitor-shared/lib/showProductPromotions";
import { PRICE_TEXT_SURFACE } from "@faire/retailer-visitor-shared/views/product/ProductDetails/Price/PriceProps";
import { Spacer } from "@faire/web--source/slate/spacing";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import React, { useContext } from "react";

import { CaselineProps } from "./__internal__/components/CaseSizeText/CaselineProps";
import { productOnlyImagePropsILeanProductTileAdapter } from "./__internal__/components/ProductImage/productOnly/productOnlyImagePropsILeanProductTileAdapter";
import { useProductOnlyProductTileImageProps } from "./__internal__/components/ProductImage/productOnly/useProductOnlyTileImageProps";
import { OrderedCount } from "./__internal__/components/ProductInfo/OrderedCount";
import { ProductLinkForProductOnlyTile } from "./__internal__/components/ProductLink/ProductLinkForProductOnlyTile";

export const LoggedInProductOnlyProductTile: React.FC<{
  productTile: ILeanProductTile;
  brandName: string | undefined;
  brandToken?: string | undefined;
  showVerticalImagery?: boolean;
}> = ({ productTile, brandName, brandToken, showVerticalImagery }) => {
  const { productTileSurface } = useContext(ProductTileSurfaceContext);
  const isLoggedInOrBrandPreview = useIsLoggedInRetailerOrBrandPreview();
  const productTileImageProps = useProductOnlyProductTileImageProps(
    productOnlyImagePropsILeanProductTileAdapter(
      productTile,
      brandName ?? "",
      showVerticalImagery
    )
  );

  const token = productTile.product?.token;
  if (!token) {
    // TODO loading state should probably live in the composable elements
    return null;
  }

  const trackingWrapperProps =
    productTileTrackingWrapperPropsILeanProductTileAdapter(productTile);
  const productTileDebugInfoProps =
    debugInfoSectionPropsILeanProductTileAdapter(productTile);

  const productTileOverlayProps = productTileOverlayProductTileAdaptor(
    productTile,
    true
  );
  const topBadgeProps =
    productTileTopBadgesPropsILeanProductTileAdapter(productTile);

  const brandPriceTextProps: ClientFormatMoneyPriceWrapperProps = {
    min_option_wholesale_price: productTile.min_option_wholesale_price,
    min_option_wholesale_promo_price:
      productTile.min_option_wholesale_promo_price,
    min_option_retail_price: productTile.min_option_retail_price,
    showProductPromotions: showProductPromotions(
      productTile.min_option_wholesale_promo_price
    ),
    small: true,
    isLoggedInOrBrandPreview,
    isTilePricing: true,
    priceTextSurface: PRICE_TEXT_SURFACE.BRAND,
  };

  const caseLineProps: CaselineProps = {
    caseSizeText: productTile.case_size_text,
    availabilityText:
      // Do not show availability text for tiles in Brand Search on Search Page
      // for the NEXTJS_RSC_SDUI_LOGGED_IN_SEARCH_PAGE_250122 migration to maintain
      // consistency between treatment and control groups
      productTileSurface !== ProductTileSurface.SEARCH_PAGE
        ? productTile.availability_info
        : undefined,
    isLoggedIn: true,
  };
  const quickAddProps =
    productTileQuickAddPropsILeanProductTileAdapter(productTile);
  const favoriteProps =
    productTileFavoritePropsILeanProductTileAdapter(productTile);

  return (
    <ProductTileTrackingWrapper
      data={trackingWrapperProps}
      brandToken={brandToken}
    >
      <ProductTileContextProvider brandToken={brandToken} brand={undefined}>
        <ProductTileContainer>
          <ProductLinkForProductOnlyTileBySurface
            productTileSurface={productTileSurface}
            productTile={productTile}
            showVerticalImagery={showVerticalImagery}
          >
            <ProductTileContainerRefContextProvider>
              <ProductTileUpperContainer>
                <MaybeBrandPageImageErrorBoundaryWrapper enabled={true}>
                  <ProductImage data={productTileImageProps} />
                </MaybeBrandPageImageErrorBoundaryWrapper>
                <ProductTileOverlay data={productTileOverlayProps} />
                <TopBadges data={topBadgeProps} />
                <DebugInfoSection data={productTileDebugInfoProps} />
                <NotifyMe data={notifyMeProductTileAdaptor(productTile)} />
                <QuickAdd showOnHover={false} data={quickAddProps} />
                <Favorite data={favoriteProps} />
              </ProductTileUpperContainer>
            </ProductTileContainerRefContextProvider>
            <Spacer size={{ mobileAndAbove: "1x", tabletAndAbove: 12 }} />
            <PriceWithClientFormatMoney {...brandPriceTextProps} />
            <ProductName productName={productTile?.product?.name ?? ""} />
            {/* Adjust the position of the Caseline for tiles in Brand Search on 
            Search Page for the NEXTJS_RSC_SDUI_LOGGED_IN_SEARCH_PAGE_250122 
            migration to maintain consistency between treatment and control groups */}
            {productTileSurface === ProductTileSurface.SEARCH_PAGE ? (
              <Caseline {...caseLineProps} />
            ) : null}
            <ProductTileSwatches optionDetails={productTile.options_details} />
            {productTileSurface !== ProductTileSurface.SEARCH_PAGE ? (
              <Caseline {...caseLineProps} />
            ) : null}
            <ExpectedShipDate shipDate={productTile.expected_shipping_date} />
            <OrderedCount
              orderedCountMessage={productTile.product?.order_count_message}
            />
          </ProductLinkForProductOnlyTileBySurface>
        </ProductTileContainer>
      </ProductTileContextProvider>
    </ProductTileTrackingWrapper>
  );
};

const ProductLinkForProductOnlyTileBySurface = ({
  productTileSurface,
  productTile,
  showVerticalImagery,
  children,
}: {
  productTileSurface: ProductTileSurface | undefined;
  productTile: ILeanProductTile;
  showVerticalImagery: boolean | undefined;
  children: React.ReactNode;
}) => {
  if (productTileSurface === ProductTileSurface.SEARCH_PAGE) {
    const productLinkProps =
      productTileClickProviderPropsILeanProductTileAdapter(productTile);
    return (
      <SearchProductLink data={productLinkProps}>{children}</SearchProductLink>
    );
  }
  const productLinkProps = productLinkPropsAdapter(
    productTile,
    showVerticalImagery
  );
  return (
    <ProductLinkForProductOnlyTile data={productLinkProps}>
      {children}
    </ProductLinkForProductOnlyTile>
  );
};
